import {createPortal, ThreeEvent, useFrame, useStore, useThree} from "@react-three/fiber"
import {forwardRef, ReactNode, Suspense, useContext, useEffect, useImperativeHandle, useMemo, useRef} from "react"
import {PointerCursorModel, usePointerXRInputSourceEvents, useRayPointer, useXR, XRControllerState, XRSpace, DefaultXRControllerOptions, useXRInputSourceStateContext, XRControllerModel, DefaultXRInputSourceRayPointerOptions, useLinesPointer} from "@react-three/xr"
import {xrInputSourceStateContext} from "@react-three/xr/dist/contexts.js";
import {BufferGeometry, Camera, ColorRepresentation, Euler, Mesh, Object3D, Object3DEventMap, QuadraticBezierCurve3, Quaternion, Group, Vector3, Vector3Tuple} from "three"
import {MeshLineGeometry, MeshLineMaterial} from "meshline"
import {Pointer, PointerEventsMap, PointerEvent, RayPointerOptions} from "@pmndrs/pointer-events"
import {clamp} from "three/src/math/MathUtils.js"
import {AllowedPointerEvents, AllowedPointerEventsType, PointerOptions} from "@pmndrs/pointer-events/dist/pointer"
import React from "react";
import {IntersectionEvent} from "@react-three/fiber/dist/declarations/src/core/events.js"
import * as THREE from "three";
import {shallow} from "zustand/shallow";
import {store} from "../../../Store/index.js";
import {GetControllerSide_Final} from "../../../Store/main/settings/vr.js";

/** Component that allows to declare its children as teleport targets. */
export function TeleportTarget({
	children,
	onTeleport,
}: {
	children?: ReactNode
	onTeleport?: (point: Vector3, event: ThreeEvent<MouseEvent>) => void
}) {
	const ref = useRef<Group>(null)
	const teleportRef = useRef(onTeleport)
	teleportRef.current = onTeleport
	const store = useStore()
	useEffect(()=>{
		if (ref.current == null) {
			return
		}
		return makeTeleportTarget(
			ref.current,
			()=>store.getState().camera,
			(point, event)=>teleportRef.current?.(point, event as any),
		)
	}, [store])
	return (
		<group pointerEventsType={{allow: "teleport"}} ref={ref}>
			{children}
		</group>
	)
}

export const TeleportPointerRayModel = forwardRef<
	TeleportPointerRayModelImpl,
	TeleportPointerRayModelOptions & {pointer: Pointer; linePoints: Array<Vector3>}
>(({pointer, linePoints, ...options}, ref)=>{
	const mesh = useMemo(()=>new TeleportPointerRayModelImpl(linePoints), [linePoints])
	useImperativeHandle(ref, ()=>mesh, [mesh])
	mesh.options = options
	useFrame(()=>mesh.update(pointer))
	return <primitive object={mesh} />
})

/**
 * marks its children as teleportable
 */
export function makeTeleportTarget(
	root: Object3D<Object3DEventMap & PointerEventsMap>,
	camera: Camera | (() => Camera),
	onTeleport: (pointer: Vector3, event: PointerEvent) => void,
) {
	root.traverse(object=>(object.userData.teleportTarget = true))

	const listener = (e: PointerEvent & THREE.Event<"pointerup", Object3D<Object3DEventMap & PointerEventsMap>>)=>{
		if (e.pointerState.inputSource.handedness != GetControllerSide_Final("left")) return;
		if ("point" in e && e.point instanceof Vector3) {
			const c = typeof camera === "function" ? camera() : camera
			const point = new Vector3().setFromMatrixPosition(c.matrix).negate().setComponent(1, 0).add(e.point)
			onTeleport(point, e as any)
		}
	}
	root.addEventListener("pointerup", listener)

	/*let listener;
	let set = function<T>(val: T): T {listener = val; return val;};
	root.addEventListener('pointerup', set(e=>{
		if (e.pointerState.inputSource.handedness != "left") return;
		if ('point' in e && e.point instanceof Vector3) {
			const c = typeof camera === 'function' ? camera() : camera
			const point = new Vector3().setFromMatrixPosition(c.matrix).negate().setComponent(1, 0).add(e.point)
			onTeleport(point, e as any)
		}
	}));*/

	return ()=>{
		root.traverse(object=>(object.userData.teleportTarget = false))
		root.removeEventListener("pointerup", listener)
	}
}

const eulerHelper = new Euler(0, 0, 0, "YXZ")
const quaternionHelper = new Quaternion()

/**
 * @param space
 * @param rayGroup must be placed directly into the scene
 */
export function syncTeleportPointerRayGroup(space: Object3D, rayGroup: Object3D, deltaTimeMs: number) {
	space.updateWorldMatrix(true, false)
	space.matrixWorld.decompose(rayGroup.position, quaternionHelper, rayGroup.scale)
	eulerHelper.setFromQuaternion(quaternionHelper)
	eulerHelper.z = 0
	eulerHelper.x = clamp(eulerHelper.x - (10 * Math.PI) / 180, -Math.PI / 2, (1.1 * Math.PI) / 4)
	quaternionHelper.setFromEuler(eulerHelper)
	rayGroup.quaternion.slerp(quaternionHelper, deltaTimeMs / 100)
}

/**
 * check if the object is marked as teleportable
 */
export function isTeleportTarget(object: Object3D): boolean {
	return object.userData.teleportTarget === true
}

export function buildTeleportTargetFilter(options: PointerOptions = {}) {
	return (
		object: Object3D,
		pointerEvents: AllowedPointerEvents,
		pointerEventsType: AllowedPointerEventsType,
		pointerEventsOrder: number,
	)=>{
		if (!isTeleportTarget(object)) {
			return false
		}
		if (options.filter != null && !options.filter(object, pointerEvents, pointerEventsType, pointerEventsOrder)) {
			return false
		}
		return true
	}
}

/** Lib's lines-intersector sorts by distance-within-the-hit-SEGMENT (not along the whole arc), so a ground hit early in a late segment beat an
 * elevated target (eg. loft floor) hit earlier on the arc. This sort uses the cumulative distance along the (world-space) line instead. */
export function buildLinesCumulativeDistanceSort(getSpace: ()=>Object3D | null, linePoints: Vector3[]) {
	const worldPoints = linePoints.map(()=>new Vector3());
	const segStart = new Vector3();
	const segDir = new Vector3();
	const toPoint = new Vector3();
	const closest = new Vector3();
	const getCumulativeDistance = (point: Vector3)=>{
		let best = Infinity, bestCumDist = Infinity, cumDist = 0;
		for (let i = 0; i < worldPoints.length - 1; i++) {
			segStart.copy(worldPoints[i]);
			segDir.copy(worldPoints[i + 1]).sub(segStart);
			const segLength = segDir.length();
			const t = segLength > 0 ? clamp(toPoint.copy(point).sub(segStart).dot(segDir) / (segLength * segLength), 0, 1) : 0;
			closest.copy(segStart).addScaledVector(segDir, t);
			const offLine = closest.distanceToSquared(point);
			if (offLine < best) {
				best = offLine;
				bestCumDist = cumDist + t * segLength;
			}
			cumDist += segLength;
		}
		return bestCumDist;
	};
	return (i1: {point: Vector3}, order1: number | undefined, i2: {point: Vector3}, order2: number | undefined)=>{
		if ((order1 ?? 0) != (order2 ?? 0)) return (order2 ?? 0) - (order1 ?? 0); // higher pointer-events-order first (matches the lib's default)
		const space = getSpace();
		if (space == null) return 0;
		for (let i = 0; i < linePoints.length; i++) worldPoints[i].copy(linePoints[i]).applyMatrix4(space.matrixWorld);
		return getCumulativeDistance(i1.point) - getCumulativeDistance(i2.point);
	};
}

export function createTeleportRayLine() {
	const curve = new QuadraticBezierCurve3(new Vector3(0, 0, 0), new Vector3(0, 0, -8), new Vector3(0, -20, -15))
	return curve.getPoints(20)
}

export type TeleportPointerRayModelOptions = {
	/**
	 * @default white
	 */
	color?: ColorRepresentation | Vector3Tuple | ((pointer: Pointer) => ColorRepresentation | Vector3Tuple)
	/**
	 * @default 0.4
	 */
	opacity?: number | ((pointer: Pointer) => number)
	/**
	 * @default 0.01
	 */
	size?: number
}

export class TeleportPointerRayModelImpl extends Mesh<BufferGeometry, MeshLineMaterial> {
	private readonly multiplier: number
	private lineLengths: Array<number>

	public options: TeleportPointerRayModelOptions = {}

	constructor(points: Array<Vector3>) {
		const geometry = new MeshLineGeometry()
		const float32Array = new Float32Array(points.length * 3)
		for (let i = 0; i < points.length; i++) {
			points[i].toArray(float32Array, i * 3)
		}
		geometry.setPoints(float32Array)
		const multiplier = (points.length * 3 - 3) / (points.length * 3 - 1)
		const material = new MeshLineMaterial({
			lineWidth: 0.1,
			resolution: undefined as any,
			visibility: multiplier,
		})
		super(geometry, material)
		this.material.transparent = true
		this.multiplier = multiplier
		this.material = material
		this.lineLengths = points.slice(0, -1).map((p, i)=>p.distanceTo(points[i + 1]))
	}

	update(pointer: Pointer): void {
		const enabled = pointer.getEnabled()
		const intersection = pointer.getIntersection()
		//console.log("Pointer.buttonsDown:", [...pointer.getButtonsDown()]);
		if (!enabled || pointer.getButtonsDown().size == 0 || intersection == null) {
			this.visible = false;
			return;
		}

		this.visible = true
		if (intersection.details.type != "lines") {
			this.material.visibility = this.multiplier;
			return;
		}

		const {distanceOnLine, lineIndex} = intersection.details
		const lineLength = this.lineLengths[lineIndex]
		this.material.visibility = (this.multiplier * (lineIndex + distanceOnLine / lineLength)) / this.lineLengths.length
		const {color = "white", opacity = 0.4, size = 0.01} = this.options
		this.material.lineWidth = size
		this.material.opacity = typeof opacity === "function" ? opacity(pointer) : opacity
		const resolvedColor = typeof color === "function" ? color(pointer) : color
		if (Array.isArray(resolvedColor)) {
			this.material.color.set(...resolvedColor)
		} else {
			this.material.color.set(resolvedColor)
		}
	}
}

export type DefaultXRInputSourceTeleportPointerOptions = Omit<RayPointerOptions, "lines"> & {
	makeDefault?: boolean
	rayModel?: boolean | TeleportPointerRayModelOptions
	cursorModel?: boolean | PointerCursorModelOptions
 }
 export type PointerCursorModelOptions = {
	color?: ColorRepresentation | Vector3Tuple | ((pointer: Pointer) => ColorRepresentation | Vector3Tuple)
	opacity?: number | ((pointer: Pointer) => number)
	size?: number
	renderOrder?: number
	cursorOffset?: number
	materialClass?: { new (): THREE.MeshBasicMaterial }
 }

/**
 * telport pointer for the XRInputSource
 * emits a downwards bend ray that only interesects with meshes marked as teleportable
 *
 * properties
 * - `clickThresholdMs` time in milliseconds between pointerdown and pointerup to trigger a click event
 * - `dblClickThresholdMs` time in milliseconds between the first click and the second click to trigger a dblclick event
 * - `contextMenuButton` the button that triggers contextmenu events
 * - `makeDefault` used the set the default pointer inside a combined pointer
 * - `radius` the size of the intersection sphere
 * - `minDistance` minimal distance to trigger interactions
 * - `direction` the direction of the ray
 * - `rayModel` properties for configuring how the ray should look
 * - `cursorModel` properties for configuring how the cursor should look
 */
export function XRInputSourceTeleportPointer(options: DefaultXRInputSourceTeleportPointerOptions) {
	const state = useContext(xrInputSourceStateContext)
	if (state == null) {
	  throw new Error(`XRInputSourceRayPointer can only be used inside a XRInputSource`)
	}
	const ref = useRef<Object3D>(null)
	const groupRef = useRef<Group>(null)
	const linePoints = useMemo(()=>createTeleportRayLine(), [])
	const customSort = useMemo(()=>buildLinesCumulativeDistanceSort(()=>groupRef.current, linePoints), [linePoints])
	const pointer = useLinesPointer(
	  groupRef,
	  state,
	  {
		 ...options,
		 linePoints,
		 customSort: options.customSort ?? customSort as any,
		 filter: buildTeleportTargetFilter(options),
	  },
	  "teleport",
	)
	usePointerXRInputSourceEvents(pointer, state.inputSource, "select", state.events)
	const rayModelOptions = options.rayModel
	const cursorModelOptions = options.cursorModel
	const scene = useThree(state=>state.scene)
	const cursorRef = useRef<Mesh>(null)
	useFrame((_, delta)=>{
	  if (cursorRef.current != null) {
		 cursorRef.current.visible = pointer.getEnabled() && pointer.getButtonsDown().size > 0
	  }
	  const target = groupRef.current
	  const source = ref.current
	  if (target == null || source == null) {
		 return
	  }
	  syncTeleportPointerRayGroup(source, target, delta * 1000)
	})
	return (
	  <>
		 <XRSpace ref={ref} space="target-ray-space" />
		 {createPortal(
			<group ref={groupRef}>
			  {rayModelOptions !== false && (
				 <TeleportPointerRayModel
					linePoints={linePoints}
					pointer={pointer}
					opacity={.5}
					{...spreadable(rayModelOptions)}
				 />
			  )}
			  {cursorModelOptions !== false && (
				 <PointerCursorModel
					ref={cursorRef}
					pointer={pointer}
					opacity={.5}
					{...spreadable(cursorModelOptions)}
				 />
			  )}
			</group>,
			scene,
		 )}
	  </>
	)
}

function spreadable<T>(value: true | T): T | undefined {
	if (value === true) {
	  return undefined
	}
	return value
}